import React, { useEffect, useState } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import { Button, Modal, Spin, Input, Form, List, message } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

const AgentRegistrationManagement = () => {
  const [formResponses, setFormResponses] = useState([]);
  const apiUrl = import.meta.env.VITE_API_URL;
  const [formFields, setFormFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [deletingResponse, setDeletingResponse] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contentData, setContentData] = useState(null);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [newField, setNewField] = useState({
    name: "",
    field_type: "text",
    required: false,
    options: [],
    priority: 0,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch form fields
        const fieldsResponse = await axios.get(`${apiUrl}/form-fields/1/`);
        const sortedFields = fieldsResponse.data.sort((a, b) => a.priority - b.priority);
        setFormFields(sortedFields);
        
        // Fetch form responses
        const responsesResponse = await axios.get(`${apiUrl}/form_responses/?form_id=1`);
        const sortedResponses = (responsesResponse.data || []).sort(
          (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)
        );
        setFormResponses(sortedResponses);
        
        // Fetch content data
        const contentResponse = await axios.get(`${apiUrl}/form-info/agent-registration/`);
        setContentData(contentResponse.data);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setErrorMessage("Failed to fetch data. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  const handleDeleteResponse = (responseId) => {
    setDeletingResponse(responseId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/form_responses/${deletingResponse}/`);
      setFormResponses(formResponses.filter((response) => response.id !== deletingResponse));
      setIsDeleteModalOpen(false);
      message.success("Form response deleted successfully!");
    } catch (error) {
      console.error("Error deleting form response:", error);
      message.error("Failed to delete the form response. Please try again.");
    }
  };

  const handleAddField = async () => {
    if (!newField.name) {
      message.warning("Field name is required.");
      return;
    }

    if (["select", "radio", "checkbox"].includes(newField.field_type) && newField.options.length === 0) {
      message.warning("Please provide at least one option for select/radio/checkbox fields.");
      return;
    }

    try {
      const payload = {
        ...newField,
        form: 1,
      };

      const response = await axios.post(`${apiUrl}/form-fields/1/`, payload);
      setFormFields([...formFields, response.data]);
      setNewField({ name: "", field_type: "text", required: false, options: [], priority: 0 });
      message.success("Field added successfully!");
    } catch (error) {
      console.error("Error adding field:", error.response?.data || error);
      message.error("Failed to add the field. Please try again.");
    }
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.patch(`${apiUrl}/form-fields/1/${editingField.id}/`, editingField);
      setFormFields(formFields.map((field) => (field.id === editingField.id ? response.data : field)));
      setEditModalOpen(false);
      message.success("Field updated successfully!");
    } catch (error) {
      console.error("Error updating field:", error);
      message.error("Failed to update the field. Please try again.");
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      await axios.delete(`${apiUrl}/form-fields/1/${fieldId}/`);
      setFormFields(formFields.filter((field) => field.id !== fieldId));
      message.success("Field deleted successfully!");
    } catch (error) {
      console.error("Error deleting field:", error);
      message.error("Failed to delete the field. Please try again.");
    }
  };

  const handleMoveField = async (fieldId, direction) => {
    const fieldIndex = formFields.findIndex((field) => field.id === fieldId);
    if (
      (direction === "up" && fieldIndex === 0) ||
      (direction === "down" && fieldIndex === formFields.length - 1)
    ) {
      return;
    }

    const updatedFields = [...formFields];
    const newIndex = direction === "up" ? fieldIndex - 1 : fieldIndex + 1;
    [updatedFields[fieldIndex], updatedFields[newIndex]] = [
      updatedFields[newIndex],
      updatedFields[fieldIndex],
    ];

    updatedFields.forEach((field, index) => {
      field.priority = index;
    });

    try {
      await axios.patch(`${apiUrl}/form-fields/1/${fieldId}/`, {
        priority: newIndex,
      });

      await Promise.all(
        updatedFields.map((field) =>
          axios.patch(`${apiUrl}/form-fields/1/${field.id}/`, {
            priority: field.priority,
          })
        )
      );

      setFormFields(updatedFields);
    } catch (error) {
      console.error("Error updating priority:", error);
      message.error("Failed to update priority. Please try again.");
    }
  };

  const handleOpenContentModal = () => {
    setEditingContent(JSON.parse(JSON.stringify(contentData)));
    setIsContentModalOpen(true);
  };

  const handleContentChange = (field, value) => {
    setEditingContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...editingContent.features];
    updatedFeatures[index] = {
      ...updatedFeatures[index],
      [field]: value
    };
    handleContentChange('features', updatedFeatures);
  };

  const handleWhyJoinChange = (field, value) => {
    handleContentChange('whyJoin', {
      ...editingContent.whyJoin,
      [field]: value
    });
  };

  const handleAddFeature = () => {
    handleContentChange('features', [
      ...editingContent.features,
      { icon: "", title: "", text: "" }
    ]);
  };

  const handleRemoveFeature = (index) => {
    const updatedFeatures = [...editingContent.features];
    updatedFeatures.splice(index, 1);
    handleContentChange('features', updatedFeatures);
  };

  const handleAddWhyJoinItem = () => {
    handleWhyJoinChange('items', [
      ...editingContent.whyJoin.items,
      ""
    ]);
  };

  const handleRemoveWhyJoinItem = (index) => {
    const updatedItems = [...editingContent.whyJoin.items];
    updatedItems.splice(index, 1);
    handleWhyJoinChange('items', updatedItems);
  };

  const handleSaveContent = async () => {
    try {
      await axios.patch(`${apiUrl}/form-info/agent-registration/`, editingContent);
      setContentData(editingContent);
      setIsContentModalOpen(false);
      message.success('The registration page content has been successfully updated.');
    } catch (error) {
      console.error("Error updating content:", error);
      message.error('There was an error updating the content. Please try again.');
    }
  };

  const csvData = formResponses.map((response) => {
    const row = { "Submitted At": new Date(response.submitted_at).toLocaleString() };
    formFields.forEach((field) => {
      const fieldResponse = response.field_responses.find((fr) => fr.form_field.id === field.id);
      row[field.name] = fieldResponse
        ? field.field_type === "image"
          ? fieldResponse.file
          : fieldResponse.value || "N/A"
        : "N/A";
    });
    return row;
  });

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-4xl font-bold mb-6">Agent Registration Management</h1>
      
      {isLoading ? (
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      ) : errorMessage ? (
        <p className="text-red-500">{errorMessage}</p>
      ) : (
        <>
          {/* Content Management Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Page Content</h2>
            {contentData ? (
              <div>
                <Button 
                  type="primary" 
                  onClick={handleOpenContentModal}
                  className="mb-4"
                >
                  Edit Page Content
                </Button>
                <div className="border p-4 rounded">
                  <h3 className="font-bold mb-2">Current Content:</h3>
                  <p><strong>Title:</strong> {contentData.title}</p>
                  <div className="mt-4">
                    <h4 className="font-bold">Features:</h4>
                    <List
                      dataSource={contentData.features}
                      renderItem={(item, index) => (
                        <List.Item key={index}>
                          <div>
                            <p><strong>Icon:</strong> {item.icon}</p>
                            <p><strong>Title:</strong> {item.title}</p>
                            <p><strong>Text:</strong> {item.text}</p>
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                  {contentData.whyJoin && (
                    <div className="mt-4">
                      <h4 className="font-bold">Why Join Section:</h4>
                      <p><strong>Title:</strong> {contentData.whyJoin.title}</p>
                      <ul className="list-disc pl-5">
                        {contentData.whyJoin.items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Spin size="large" />
            )}
          </div>

          {/* Form Fields Management Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Form Fields</h2>
            {formFields.length === 0 ? (
              <p>No fields available.</p>
            ) : (
              <ul>
                {formFields.map((field, index) => (
                  <li key={field.id} className="flex justify-between items-center mb-4 border-b pb-2">
                    <div>
                      <p className="font-bold">{field.name}</p>
                      <p className="text-sm text-gray-500">
                        Type: {field.field_type} | Priority: {field.priority} | Required: {field.required ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        icon={<ArrowUpOutlined />}
                        className="bg-gray-200 text-gray-700 py-1 px-2 rounded-md hover:bg-gray-300"
                        onClick={() => handleMoveField(field.id, "up")}
                        disabled={index === 0}
                      />
                      <Button
                        icon={<ArrowDownOutlined />}
                        className="bg-gray-200 text-gray-700 py-1 px-2 rounded-md hover:bg-gray-300"
                        onClick={() => handleMoveField(field.id, "down")}
                        disabled={index === formFields.length - 1}
                      />
                      <button
                        className="bg-yellow-500 text-white py-1 px-3 rounded-md hover:bg-yellow-600"
                        onClick={() => handleEditField(field)}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-red-500 text-white py-1 px-3 rounded-md hover:bg-red-600"
                        onClick={() => handleDeleteField(field.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Add New Field Form */}
            <div className="mt-8">
              <h3 className="text-xl font-bold mb-4">Add New Field</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                  <Input
                    type="text"
                    value={newField.name}
                    onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    className="w-full border border-gray-300 rounded-md p-2"
                    value={newField.field_type}
                    onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="image">Image</option>
                    <option value="textarea">TextArea</option>
                    <option value="select">Dropdown</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="radio">Radio (Single Choice)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <Input
                    type="number"
                    value={newField.priority}
                    onChange={(e) => setNewField({ ...newField, priority: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="flex items-center">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={newField.required}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                    />
                    <span className="ml-2">Required</span>
                  </label>
                </div>
              </div>

              {["select", "radio", "checkbox"].includes(newField.field_type) && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                  {newField.options.map((option, index) => (
                    <div key={index} className="flex mb-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const updatedOptions = [...newField.options];
                          updatedOptions[index] = e.target.value;
                          setNewField({ ...newField, options: updatedOptions });
                        }}
                      />
                      <Button
                        danger
                        onClick={() => {
                          const updatedOptions = newField.options.filter((_, i) => i !== index);
                          setNewField({ ...newField, options: updatedOptions });
                        }}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => setNewField({ ...newField, options: [...newField.options, ""] })}
                    className="w-full"
                  >
                    Add Option
                  </Button>
                </div>
              )}

              <Button
                type="primary"
                onClick={handleAddField}
                className="mt-4"
              >
                Add Field
              </Button>
            </div>
          </div>

          {/* Form Responses Section */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Form Responses</h2>
            {formResponses.length === 0 ? (
              <p>No form responses available.</p>
            ) : (
              <>
                <CSVLink 
                  data={csvData} 
                  filename="agent_registration_responses.csv" 
                  className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 mb-4 inline-block"
                >
                  Export to CSV
                </CSVLink>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 border-b text-left">Submitted At</th>
                        {formFields.map((field) => (
                          <th key={field.id} className="py-2 px-4 border-b text-left">
                            {field.name}
                          </th>
                        ))}
                        <th className="py-2 px-4 border-b text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formResponses.map((response) => (
                        <tr key={response.id} className="hover:bg-gray-50">
                          <td className="py-2 px-4 border-b">
                            {new Date(response.submitted_at).toLocaleString()}
                          </td>
                          {formFields.map((field) => {
                            const fieldResponse = response.field_responses.find(
                              (fr) => fr.form_field.id === field.id
                            );
                            return (
                              <td key={field.id} className="py-2 px-4 border-b">
                                {fieldResponse ? (
                                  field.field_type === "image" ? (
                                    <div className="flex items-center">
                                      <img
                                        src={fieldResponse.file}
                                        className="w-12 h-12 object-cover rounded-full mr-2"
                                        alt="Uploaded"
                                      />
                                      <a
                                        href={fieldResponse.file}
                                        download
                                        className="text-blue-500 hover:underline"
                                      >
                                        Download
                                      </a>
                                    </div>
                                  ) : (
                                    fieldResponse.value || "N/A"
                                  )
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            );
                          })}
                          <td className="py-2 px-4 border-b">
                            <Button
                              danger
                              onClick={() => handleDeleteResponse(response.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Edit Field Modal */}
      <Modal
        title="Edit Field"
        visible={editModalOpen}
        onOk={handleSaveEdit}
        onCancel={() => setEditModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>,
        ]}
      >
        {editingField && (
          <Form layout="vertical">
            <Form.Item label="Field Name">
              <Input
                value={editingField.name}
                onChange={(e) =>
                  setEditingField({ ...editingField, name: e.target.value })
                }
              />
            </Form.Item>
            <Form.Item label="Field Type">
              <select
                className="w-full border border-gray-300 rounded-md p-2"
                value={editingField.field_type}
                onChange={(e) =>
                  setEditingField({ ...editingField, field_type: e.target.value })
                }
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="image">Image</option>
                <option value="textarea">TextArea</option>
                <option value="select">Dropdown</option>
                <option value="checkbox">Checkbox</option>
                <option value="radio">Radio (Single Choice)</option>
              </select>
            </Form.Item>
            <Form.Item label="Priority">
              <Input
                type="number"
                value={editingField.priority}
                onChange={(e) =>
                  setEditingField({
                    ...editingField,
                    priority: parseInt(e.target.value) || 0,
                  })
                }
              />
            </Form.Item>
            <Form.Item>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="form-checkbox"
                  checked={editingField.required}
                  onChange={(e) =>
                    setEditingField({ ...editingField, required: e.target.checked })
                  }
                />
                <span className="ml-2">Required</span>
              </label>
            </Form.Item>
            {["select", "radio", "checkbox"].includes(editingField.field_type) && (
              <Form.Item label="Options">
                {editingField.options?.map((option, index) => (
                  <div key={index} className="flex mb-2">
                    <Input
                      value={option}
                      onChange={(e) => {
                        const updatedOptions = [...editingField.options];
                        updatedOptions[index] = e.target.value;
                        setEditingField({ ...editingField, options: updatedOptions });
                      }}
                    />
                    <Button
                      danger
                      onClick={() => {
                        const updatedOptions = editingField.options.filter(
                          (_, i) => i !== index
                        );
                        setEditingField({ ...editingField, options: updatedOptions });
                      }}
                      className="ml-2"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    setEditingField({
                      ...editingField,
                      options: [...editingField.options, ""],
                    })
                  }
                  className="w-full"
                >
                  Add Option
                </Button>
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>

      {/* Content Editing Modal */}
      <Modal
        title="Edit Page Content"
        visible={isContentModalOpen}
        onOk={handleSaveContent}
        onCancel={() => setIsContentModalOpen(false)}
        width={800}
        footer={[
          <Button key="back" onClick={() => setIsContentModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSaveContent}>
            Save Changes
          </Button>,
        ]}
      >
        {editingContent && (
          <Form layout="vertical">
            <Form.Item label="Title">
              <Input
                value={editingContent.title}
                onChange={(e) => handleContentChange('title', e.target.value)}
              />
            </Form.Item>

            <h3 className="font-bold mb-2">Features</h3>
            {editingContent.features?.map((feature, index) => (
              <div key={index} className="border p-4 mb-4 rounded">
                <Form.Item label="Icon">
                  <Input
                    value={feature.icon}
                    onChange={(e) => handleFeatureChange(index, 'icon', e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Title">
                  <Input
                    value={feature.title}
                    onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Text">
                  <Input.TextArea
                    value={feature.text}
                    onChange={(e) => handleFeatureChange(index, 'text', e.target.value)}
                  />
                </Form.Item>
                <Button
                  danger
                  onClick={() => handleRemoveFeature(index)}
                  className="mt-2"
                >
                  Remove Feature
                </Button>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={handleAddFeature}
              className="w-full mb-6"
            >
              Add Feature
            </Button>

            <h3 className="font-bold mb-2">Why Join Us Section</h3>
            <Form.Item label="Section Title">
              <Input
                value={editingContent.whyJoin?.title}
                onChange={(e) => handleWhyJoinChange('title', e.target.value)}
              />
            </Form.Item>
            {editingContent.whyJoin?.items?.map((item, index) => (
              <div key={index} className="flex items-center mb-2">
                <Input
                  value={item}
                  onChange={(e) => {
                    const newItems = [...editingContent.whyJoin.items];
                    newItems[index] = e.target.value;
                    handleWhyJoinChange('items', newItems);
                  }}
                  className="flex-1"
                />
                <Button
                  danger
                  onClick={() => handleRemoveWhyJoinItem(index)}
                  className="ml-2"
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={handleAddWhyJoinItem}
              className="w-full"
            >
              Add Benefit Item
            </Button>
          </Form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        visible={isDeleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" danger onClick={handleConfirmDelete}>
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this form response? This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default AgentRegistrationManagement;